import { Location } from "react-router";

import { Filter, TableRowData } from "../../../shared";
import {
  FILTER_KEYS,
  FILTER_NAMES,
  VALUE_IDS
} from "../../Filters/types/enums";

const useTableDataFunctions = () => {
  const routeWithoutSearch = (companiesRoute: boolean, location: Location) =>
    companiesRoute && location.pathname;

  const setTableRowData = (
    response: any,
    companiesRoute: boolean,
    tableRowDataMap: any,
    setTableData: (
      valOrUpdater:
        | TableRowData[]
        | ((currVal: TableRowData[]) => TableRowData[])
    ) => void
  ) => {
    // make a copy of companies page response object to avoid error: `cannot add property 'X', object is not extensible`
    const companiesPageApiResponse = companiesRoute && { ...response?.data };
    let itemsFromResponse = companiesPageApiResponse || response?.data.items;

    tableRowDataMap && setTableData(tableRowDataMap(itemsFromResponse));

    !tableRowDataMap && setTableData(itemsFromResponse);
  };

  const setPageDataState = (
    response: any,
    companiesRoute: boolean,
    setPageData: (valOrUpdater: any) => void,
    tableRowDataMap: any
  ) => {
    if (companiesRoute && tableRowDataMap) {
      setPageData(tableRowDataMap({ ...response?.data }));
    }

    !companiesRoute && setPageData(response?.data);
  };

  const generateFilterQueryParamsKeyName = (
    key: FILTER_NAMES | string,
    value: any
  ) => {
    const id = value?.id;

    // Custom FILTER_KEYS based on id
    if (
      (key === FILTER_NAMES.Schedules || key === FILTER_NAMES.Schedule) &&
      id === VALUE_IDS.True
    ) {
      return FILTER_KEYS.NoSchedulesAssigned;
    }

    if (key === FILTER_NAMES.Schedule && id === VALUE_IDS.Global) {
      return FILTER_KEYS.GlobalScheduleAssigned;
    }

    if (key === FILTER_NAMES.Schedule && id === VALUE_IDS.Quick) {
      return FILTER_KEYS.QuickScheduleAssigned;
    }

    if (key === FILTER_NAMES.Imei && id === VALUE_IDS.True) {
      return FILTER_KEYS.NoVehicleImeiAssigned;
    }

    const keyMap: Record<string, string> = {
      [FILTER_NAMES.Imei]: FILTER_KEYS.VehicleImeis,
      [FILTER_NAMES.Schedule]: FILTER_KEYS.ScheduleNames,
      [FILTER_NAMES.Schedules]: FILTER_KEYS.ScheduleIds,
      [FILTER_NAMES.VehicleNumber]: FILTER_KEYS.VehicleIds,
      [FILTER_NAMES.DriverName]: FILTER_KEYS.DriverIds,
      [FILTER_NAMES.Company]: FILTER_KEYS.CompanyIds
    };

    if (key === FILTER_NAMES.DateRange) {
      return value.name === FILTER_KEYS.DateRangeFrom
        ? FILTER_KEYS.DateRangeFrom
        : FILTER_KEYS.DateRangeTo;
    }

    return keyMap[key] ?? key;
  };

  const generateIdValue = (value: Filter) =>
    value?.id === VALUE_IDS.Null ? "" : value?.id;

  const generateQueryParamsKeyValue = (
    key: FILTER_NAMES | string,
    value: Filter
  ) => {
    const filtersWithIdValue =
      key === FILTER_NAMES.Schedule ||
      key === FILTER_NAMES.Schedules ||
      key === FILTER_NAMES.LastDownloadAttempt ||
      key === FILTER_NAMES.DriverName ||
      key === FILTER_NAMES.VehicleNumber ||
      key === FILTER_NAMES.Company ||
      key === FILTER_NAMES.CreditStatuses;

    const dateRangeValue = key === FILTER_NAMES.DateRange && value.id;

    const quickScheduleKey =
      key === FILTER_NAMES.Schedule && value?.id === VALUE_IDS.Quick;
    const noVehicleImeiKey =
      key === FILTER_NAMES.Imei && value?.id === VALUE_IDS.True;
    const globalScheduleKey =
      key === FILTER_NAMES.Schedule && value?.id === VALUE_IDS.Global;

    const isDeactivatedVehicleKey = key === FILTER_NAMES.DeactivatedVehicles;
    const isNoDeviceImei = key === FILTER_NAMES.NoDeviceImei;

    const filtersIdValue = filtersWithIdValue && generateIdValue(value);

    if (
      quickScheduleKey ||
      noVehicleImeiKey ||
      globalScheduleKey ||
      isDeactivatedVehicleKey ||
      isNoDeviceImei
    ) {
      return VALUE_IDS.True;
    }

    return dateRangeValue || filtersIdValue || value?.name;
  };

  const getFiltersAndFormFilterQueryParams = (filterParams: any) => {
    let queryString = "";

    if (filterParams) {
      for (const [key, val] of Object.entries(filterParams)) {
        let filterQueryParamsString = "";
        const filterValue: Filter[] = val as Filter[];

        // Safety check: ensure filterValue is an array
        if (!Array.isArray(filterValue)) {
          continue;
        }

        filterValue.forEach((v: Filter) => {
          const keyAndValueString = `&${generateFilterQueryParamsKeyName(
            key,
            v
          )}=${generateQueryParamsKeyValue(key, v)}`;
          filterQueryParamsString = filterQueryParamsString + keyAndValueString;
        });

        queryString = queryString + filterQueryParamsString;
      }
    }

    return queryString;
  };

  return {
    setTableRowData,
    routeWithoutSearch,
    setPageDataState,
    getFiltersAndFormFilterQueryParams
  };
};

export default useTableDataFunctions;
